import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Header } from './Header/Header';
import styles from './VantifyWorkOrderMain.scss';
import { RequestCard } from './RequestCard/RequestCard';
import { useTranslation } from 'react-i18next';
import { LegacyButton } from './Button/Button';
import {  H4, Icon, M, Loading } from 'design-system-web';
import { ICON_SET_FONTAWESOME } from 'constants-activate';
import { attachmentProps, Attachment } from './AttachmentModal/AttachmentModal';
import {Pill} from './Pill/Pill';
import {FileInput} from './FileInput/FileInput';
import { FileReturnType, FileReturnTypeEnum, validateFiles } from '../utils/fileReaderResolver';
import { simpleDateTime, getStatusColor, STATUS_LABEL_MAP, isWorkOrderCancellable } from '../utils/WorkOrder';
import { useWorkOrder, VantifyOperation } from 'integrations-vantify-react-hooks';
import { CancelRequestModal } from './CancelModal/CancelRequestModal';
import type { WorkOrder, WorkOrderFile } from 'integrations-vantify-client-services';
import { colors } from 'constants-colors';
import { Alert, AlertType } from './Alert/Alert';
import { timeToCloseAlert } from '../constants';
import { EmptyWorkOrderState } from './EmptyWorkOrderState/EmptyWorkOrderState';
import { WorkOrderErrorState } from './WorkOrderErrorState/WorkOrderErrorState';


type AttachmentWithFileHref = Omit<attachmentProps, 'onDelete'> & {
  fileHref?: string;
};

export const VantifyWorkOrderMain: React.FC = () => {
  const { t } = useTranslation();
  const { 
    workOrders, 
    workOrderDetail, 
    fetchWorkOrders, 
    fetchWorkOrderDetails,
    fetchWorkOrderFiles,
    userId, 
    siteId, 
    cancelWorkOrder, 
    error, 
    loading,
    uploadFile,
    addWorkOrderAttachment,
    deleteWorkOrderAttachment,
    reporter,
  } = useWorkOrder();
  const [activeWorkOrderId, setActiveWorkOrderId] = useState<number | null>(null);
  const [attachments, setAttachments] = useState<AttachmentWithFileHref[]>([]);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allJobs, setAllJobs] = useState<WorkOrder[]>([]);
  const [errorFiles, setErrorFiles] = useState<Array<{ fileName: string; errorMessage: string }>>([]);
  const [deletingAttachmentIds, setDeletingAttachmentIds] = useState<Array<string | number>>([]);
  const [downloadingAttachmentIds, setDownloadingAttachmentIds] = useState<Array<string | number>>([]);
  const downloadingIdsRef = useRef<Set<string | number>>(new Set());
  const deletingIdsRef = useRef<Set<string | number>>(new Set());
  const errorMessageTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const successMessageTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleErrorMessageClear = useCallback(() => {
    if (errorMessageTimeoutRef.current) {
      clearTimeout(errorMessageTimeoutRef.current);
    }

    errorMessageTimeoutRef.current = setTimeout(() => {
      setErrorMessage(null);
    }, 20000);
  }, []);

  const scheduleSuccessMessageClear = useCallback((delay: number) => {
    if (successMessageTimeoutRef.current) {
      clearTimeout(successMessageTimeoutRef.current);
    }

    successMessageTimeoutRef.current = setTimeout(() => {
      setSuccessMessage(null);
    }, delay);
  }, []);

  useEffect(() => {
    return () => {
      if (errorMessageTimeoutRef.current) {
        clearTimeout(errorMessageTimeoutRef.current);
      }

      if (successMessageTimeoutRef.current) {
        clearTimeout(successMessageTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!workOrders?.jobs) return;

    if (workOrders.page === 1) {
      setAllJobs(workOrders.jobs);
    } else {
      setAllJobs(prev => [...prev, ...workOrders.jobs]);
    }
  }, [workOrders]);

  const workordersList = allJobs;
  const activeWorkOrder = useMemo(
    () => workOrderDetail ?? workordersList.find((wo: WorkOrder) => wo.id === activeWorkOrderId) ?? null,
    [workOrderDetail, workordersList, activeWorkOrderId]
  );
  const { statusColor, pillCircleColor } = useMemo(() => {
    const colorValue = activeWorkOrder ? getStatusColor(activeWorkOrder.status) : colors.neutral.grey900;

    return { statusColor: colorValue, pillCircleColor: colorValue };

  }, [activeWorkOrder]);

  useEffect(() => {
    if (userId && siteId && reporter?.name) {
      setAllJobs([]);
      fetchWorkOrders({ userId, siteId, reporter: reporter?.name, page: 1 });
    }
  }, [siteId, userId, reporter]);

  useEffect(() => {
    if (workordersList.length > 0 && !activeWorkOrderId) {
      setActiveWorkOrderId(workordersList[0].id);
    }
  }, [workordersList, activeWorkOrderId]);

  useEffect(() => {
    if (activeWorkOrderId && siteId) {
      const fetchDetails = async () => {
       await fetchWorkOrderDetails({ siteId: Number(siteId), jobId: activeWorkOrderId });
      };

      fetchDetails();
    }
  }, [activeWorkOrderId, siteId]);

  useEffect(() => {
    setErrorFiles([]);
    setErrorMessage(null);
    setDeletingAttachmentIds([]);
    setDownloadingAttachmentIds([]);
    downloadingIdsRef.current.clear();
    deletingIdsRef.current.clear();

    if (errorMessageTimeoutRef.current) {
      clearTimeout(errorMessageTimeoutRef.current);
    }
  }, [activeWorkOrderId]);

  const loadFiles = useCallback(async () => {
    if (!activeWorkOrderId || !siteId) {
      setAttachments([]);

      return;
    }

    const response = await fetchWorkOrderFiles({
      siteId,
      jobId: activeWorkOrderId!,
    });

    if (response?.data?.files) {
      const mappedAttachments: AttachmentWithFileHref[] = response.data.files.map(
        (file: WorkOrderFile) => {
          const resourceHref = file?._links?.resource?.href;
          const resourceType = file?._links?.resource?.type;
          const thumbnail =
            resourceHref && resourceType?.startsWith('image/') ? resourceHref : undefined;

          return {
            id: file.id,
            file_association_id: file.id,
            display_name: file.title || file.filename,
            fileHref: file.id,
            created_at: file.createdAt,
            resourceHref,
            thumbnail,
          };
        }
      );

      setAttachments(mappedAttachments);
    } else {
      setAttachments([]);
    }
  }, [activeWorkOrderId]);

  const downloadAttachment = useCallback(async (resourceHref?: string, fileName?: string) => {
    if (!resourceHref) {
      return;
    }

    const attachment = attachments?.find((att) => att.resourceHref === resourceHref);
    const attachmentId = attachment?.id;

    if (!attachmentId) {
      return;
    }

    if (downloadingIdsRef.current.has(attachmentId)) {
      return; 
    }

    downloadingIdsRef.current.add(attachmentId);
    setDownloadingAttachmentIds((prev) => [...prev, attachmentId]);

    try {
      const response = await fetch(resourceHref);

      if (!response.ok) {
        const message = response.statusText ? response.statusText : t('shared.integrations.vantify.workorder.attachment.download');

        setErrorMessage(message);
        scheduleErrorMessageClear();

        return;
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = objectUrl;
      link.download = fileName || 'attachment';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('shared.integrations.vantify.workorder.attachment.download');

      setErrorMessage(message);
      scheduleErrorMessageClear();
    } finally {
      downloadingIdsRef.current.delete(attachmentId);
      setDownloadingAttachmentIds((prev) => prev.filter((id) => id !== attachmentId));
    }
  }, [scheduleErrorMessageClear, t, attachments]);

  useEffect(() => {
    loadFiles();
  }, [activeWorkOrderId]);

  useEffect(() => {
    if (!error) {
      setErrorFiles([]);

      return;
    }

    const deleteError = error[VantifyOperation.DELETE_WORK_ORDER_ATTACHMENT];
    
    if (deleteError) {
      setErrorMessage(deleteError);
      scheduleErrorMessageClear();
    }
  }, [error, scheduleErrorMessageClear]);

  const handleNewOrderSuccess = useCallback(async (jobId: number) => {
    
    setSuccessMessage(t('shared.integrations.vantify.workorder.submitSuccess'));
    scheduleSuccessMessageClear(timeToCloseAlert);
    
    if (userId && siteId && reporter?.name) {
      setAllJobs([]);
      await fetchWorkOrders({ userId, siteId, reporter: reporter?.name, page: 1 });
      setActiveWorkOrderId(jobId);
    }
  }, [userId, siteId, reporter, fetchWorkOrders, scheduleSuccessMessageClear, t]);

  const handleCancelWorkOrder = useCallback(async (note: string): Promise<boolean> => {
    if (!activeWorkOrder?.id || !siteId) return false;

    const response = await cancelWorkOrder(siteId, activeWorkOrder.id, note);

    if (response) {
      setIsCancelModalOpen(false);
      setSuccessMessage(t('shared.integrations.vantify.workorder.cancelSuccess'));
      scheduleSuccessMessageClear(timeToCloseAlert);

      if (userId && siteId && reporter?.name) {
        setAllJobs([]);
        await fetchWorkOrders({ userId, siteId, reporter: reporter?.name, page: 1 });

        if (activeWorkOrder.id) {
          await fetchWorkOrderDetails({ siteId: Number(siteId), jobId: activeWorkOrder.id });
        }
      }

      return true;
    }

    return false;
  }, [activeWorkOrder?.id, siteId, userId, reporter, cancelWorkOrder, fetchWorkOrders, fetchWorkOrderDetails, scheduleSuccessMessageClear, t]);

  const deleteFile = useCallback(async (fileId?: string | number) => {
    if (!fileId) {
      return;
    }

    const attachment = attachments?.find((att) => att.id === fileId);
    
    if (!attachment?.fileHref) {
      return;
    }

    if (deletingIdsRef.current.has(fileId)) {
      return;
    }

    deletingIdsRef.current.add(fileId);
    setDeletingAttachmentIds((prev) => [...prev, fileId]);

    try {
      const response = await deleteWorkOrderAttachment({ fileHref: attachment.fileHref });
      
      if (response) {
        setAttachments((prev) => prev.filter((att) => att.id !== fileId));
        setSuccessMessage(t('shared.integrations.vantify.workorder.attachment.deleteSuccess'));
        scheduleSuccessMessageClear(5000);

        await loadFiles();
      }
    } finally {
      deletingIdsRef.current.delete(fileId);
      setDeletingAttachmentIds((prev) => prev.filter((id) => id !== fileId));
    }
  }, [attachments, deleteWorkOrderAttachment, loadFiles, scheduleSuccessMessageClear, t]);

  const handleFilesSelected = useCallback(async (files: FileReturnType[]) => {
    if (!activeWorkOrder?.id || !siteId) {
      return;
    }

    const fileObjects = files.filter((file) => file instanceof File);

    if (fileObjects.length === 0) {
      return;
    }

    const failedFiles: Array<{ fileName: string; errorMessage: string }> = [];
    const validFiles: File[] = [];

    fileObjects.forEach((file) => {
      const validation = validateFiles(file);

      if (!validation.isValid && validation.error) {
        failedFiles.push({ fileName: file.name, errorMessage: validation.error });
      } else {
        validFiles.push(file);
      }
    });

    if (failedFiles.length > 0) {
      setErrorFiles(prev => [...prev, ...failedFiles]);
    }

    if (validFiles.length === 0) {
      return;
    }

    const uploadPromises = validFiles.map(async (file) => {
      const uploadResponse = await uploadFile({
        file,
        filename: file.name,
        contentType: file.type,
      });

      if (!uploadResponse) {
        setErrorFiles(prev => [...prev, { fileName: file.name, errorMessage: t('shared.integrations.vantify.workorder.attachment.fail') }]);

        return null;
      }

      const fileMetadata = uploadResponse?.data?.data;
      const policyKey = fileMetadata?.policy?.key;
      
      const fileNumber = policyKey.split('.')[0];
      const fileHref = `/files/${fileNumber}`;

      const addAttachmentResponse = await addWorkOrderAttachment({
        siteId,
        jobId: activeWorkOrder.id,
        fileHref,
        title: file.name,
      });

      if (!addAttachmentResponse) {
        setErrorFiles(prev => [...prev, { fileName: file.name, errorMessage: t('shared.integrations.vantify.workorder.attachment.fail') }]);

        return null;
      }

      if (addAttachmentResponse?.data) {
        return {
          id: addAttachmentResponse.data.id,
          display_name: file.name,
          fileHref,
        };
      }

      setErrorFiles(prev => [...prev, { fileName: file.name, errorMessage: t('shared.integrations.vantify.workorder.attachment.fail') }]);

      return null;
    });

    const newAttachments = (await Promise.all(uploadPromises)).filter(
      (att) => att !== null
    );

    if (newAttachments.length > 0) {
      setSuccessMessage(t('shared.integrations.vantify.workorder.attachment.success'));
      scheduleSuccessMessageClear(5000);

      await loadFiles();
    }
  }, [activeWorkOrder?.id, siteId, uploadFile, addWorkOrderAttachment, loadFiles, scheduleSuccessMessageClear, t]);

  const handlers = useMemo(() => ({
    handleWorkOrderClick: (workOrderId: number) => setActiveWorkOrderId(workOrderId),
    deleteFile,
    handleCancelRequestClick: () => setIsCancelModalOpen(true),
    handleCloseCancelModal: () => setIsCancelModalOpen(false),
  }), [deleteFile]);

  const loadMore = useCallback(async () => {
    if (!userId || !siteId || !reporter?.name || !workOrders?.page) return;

    setLoadingMore(true);
    await fetchWorkOrders({ userId, siteId, reporter: reporter.name, page: workOrders.page + 1 });
    setLoadingMore(false);
  }, [userId, siteId, reporter, workOrders?.page]);

  const isLoadingWorkOrders = loading[VantifyOperation.FETCH_WORK_ORDERS] || false;
  const isLoadingWorkOrderDetails = loading[VantifyOperation.FETCH_WORK_ORDER_DETAILS] || false;
  const isUploadingFile = loading[VantifyOperation.UPLOAD_FILE] || false;
  const isAddingAttachment = loading[VantifyOperation.ADD_WORK_ORDER_ATTACHMENT] || false;
  const isUploading = isUploadingFile || isAddingAttachment;
  const workOrderError = error?.[VantifyOperation.FETCH_WORK_ORDERS] || null;
  
  const shouldShowLoading = isLoadingWorkOrderDetails 
    || activeWorkOrderId === null 
    || !workOrderDetail
    || workOrderDetail?.id !== activeWorkOrderId;

  const handleTryAgain = useCallback(() => {
    if (userId && siteId && reporter?.name) {
      fetchWorkOrders({ userId, siteId, reporter: reporter?.name });
    }
  }, [userId, siteId, reporter]);
  
  if (isLoadingWorkOrders && allJobs.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <Loading testId="work-orders-loading" />
        <div className={styles.loadingTextContainer}>
          <H4 className={styles.loadingMainText}>{t('shared.integrations.vantify.workorder.loading.main')}</H4>
          <M className={styles.loadingSubText}>{t('shared.integrations.vantify.workorder.loading.sub')}</M>
        </div>
      </div>
    );
  }

  if (workOrderError) {
    return (
      <div className={styles.container}>
        <Header onNewOrderSuccess={handleNewOrderSuccess} disabled/>
        <WorkOrderErrorState onTryAgain={handleTryAgain} />
      </div>
    );
  }

  if (workordersList.length === 0 && !isLoadingWorkOrders && workOrders?.jobs !== undefined) {
    return (
      <div className={styles.container}>
        <Header onNewOrderSuccess={handleNewOrderSuccess} />
        {successMessage && (
          <Alert type={AlertType.success} fullWidth>
            {successMessage}
          </Alert>
        )}
        <EmptyWorkOrderState />
      </div>
    );
  }

  return (
    <>
      <div className={styles.container}>
        <Header onNewOrderSuccess={handleNewOrderSuccess} />
        {successMessage && (
          <Alert type={AlertType.success} fullWidth>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert type={AlertType.error} fullWidth>
            {errorMessage}
          </Alert>
        )}
        <div className={styles.contentBlock}>
        <div className={styles.column1}>
            {workordersList.map((workorder: WorkOrder) => (
              <div
                key={workorder.id}
                className={styles.workOrderCardWrapper}
                role="button"
                tabIndex={0}
                aria-label="Request"
                onClick={() => handlers.handleWorkOrderClick(workorder.id)}
              >
                <RequestCard
                  interaction={workorder}
                  isActive={workorder.id === activeWorkOrder?.id}
                />
              </div>
            ))}
            { workOrders?.pages && workOrders?.pages > workOrders?.page && <LegacyButton
              loading={loadingMore}
              disabled={loadingMore}
              className={styles.loadMoreButton}
              onClick={loadMore}
                                                                           >
              {t('Load more')}
            </LegacyButton> }
        </div>
        <div className={styles.column2}>
          {shouldShowLoading ? (
            <div className={styles.column2LoadingContainer}>
              <Loading testId="work-order-details-loading" />
              <div className={styles.loadingTextContainer}>
                <M className={styles.loadingSubText}>{t('shared.integrations.vantify.workorder.loading.details')}</M>
              </div>
            </div>
          ) : (
          <div className={styles.detailedViewWrapper}>
            <div className={styles.row} style={{ marginBottom: '1em' }}>
              <div className={styles.dataPointShow}>
                <div className={styles.createdAt}>
                  {t('shared.integrations.vantify.workorder.created')}
                  {simpleDateTime(activeWorkOrder?.createdAt || '')}
                </div>
                <H4 className={styles.summary}>{activeWorkOrder?.summary}</H4>
              </div>
              {isWorkOrderCancellable(activeWorkOrder) && (
                <div className={styles.row}>
                  <LegacyButton 
                    className={styles.actionsButton} 
                    size="medium"
                    onClick={handlers.handleCancelRequestClick}
                  >
                    {t('shared.integrations.vantify.cancelrequest')}
                  </LegacyButton>
                </div>
              )}
            </div>
              <Pill
                text={STATUS_LABEL_MAP[activeWorkOrder?.status ?? ''] ?? activeWorkOrder?.status ?? ''}
                size="medium"
                className={styles.pill}
                color={statusColor}
                iconName="circle"
                iconColor={pillCircleColor}
              />
            <div className={styles.dataPointShow}>
              <M className={styles.label}>{t('shared.integrations.vantify.workorder.requestdetails')}</M>
              <M className={styles.labelValue}>{activeWorkOrder?.id}</M>
              
            </div>
            <div className={styles.dataPointShow}>
              <M className={styles.label}>{t('shared.integrations.vantify.description')}</M>
              <M className={styles.labelValue}>{activeWorkOrder?.description}</M>
              
            </div>
            <div className={styles.dataPointShow}>
              <M className={styles.label}>{t('shared.integrations.vantify.category')}</M>
              <M className={styles.labelValue}>{JSON.parse(workOrderDetail?._embedded?.jobgroup?.fullPathString || "[]")[0]?.name}</M>
            </div>
            <div className={styles.dataPointShow}>
              <M className={styles.label}>{t('shared.integrations.vantify.subcategory')}</M>
              <M className={styles.labelValue}>{JSON.parse(workOrderDetail?._embedded?.jobgroup?.fullPathString || "[]")[1]?.name}</M>
            </div>
            <div className={styles.dataPoint}>
              <M className={styles.label} bold>
                {t('shared.integrations.vantify.attachments')}
              </M>
              {isWorkOrderCancellable(activeWorkOrder) && (
                <M className={styles.label}>
                  {t('shared.integrations.vantify.workorder.attachments.notice')}
                </M>
              )}
            </div>
            {isWorkOrderCancellable(activeWorkOrder) && (
              <FileInput 
                  accept="*/*" 
                  type={FileReturnTypeEnum.File} 
                  enableMultiUpload
                  onFilesSelected={handleFilesSelected}
                  disabled={!activeWorkOrder?.id || !siteId || isUploading}
              >
                  <LegacyButton 
                    className={styles.buttonAttachments} 
                    disabled={!activeWorkOrder?.id || !siteId || isUploading}
                  >
                    <div className={styles.row}>
                      <Icon
                        className={styles.emptyImageIcon}
                        set={ICON_SET_FONTAWESOME}
                        name="paperclip"
                        type="fal"
                      />
                      <M style={{ color: 'rgba(38, 27, 189, 1)' }}>
                        {t('shared.integrations.vantify.attachfile')}
                      </M>
                    </div>
                  </LegacyButton>
                </FileInput>
            )}
            {isUploading && (
                <Attachment
                  display_name={t('Uploading…')}
                  onDelete={handlers.deleteFile}
                  loading={isUploading}
                />
              )}
            {errorFiles.map((errorFile, index) => (
            <Attachment
              key={`${errorFile.fileName}-${index}`}
              display_name={errorFile.fileName}
              errorMessage={errorFile.errorMessage}
              onDelete={async (_fileId?: string | number) => {
                setErrorFiles(prev => prev.filter(ef => ef.fileName !== errorFile.fileName));
              }}
              error
            />
          ))}
           {attachments && attachments.map((attachment, index) => (
              <Attachment
                key={attachment.id ?? attachment.file_association_id ?? `attachment-${index}`}
                id={attachment.id}
                file_association_id={attachment.file_association_id}
                display_name={attachment.display_name}
                thumbnail={attachment.thumbnail}
                created_at={attachment.created_at}
                resourceHref={attachment.resourceHref}
                onDelete={handlers.deleteFile}
                downloadAttachment={downloadAttachment}
                testId="close-icon"
                deletingAttachmentIds={deletingAttachmentIds}
                downloadingAttachmentIds={downloadingAttachmentIds}
              />
            ))}
            </div>
          )}
        </div>
      </div>
      </div>
      <CancelRequestModal
        isOpen={isCancelModalOpen}
        onClose={handlers.handleCloseCancelModal}
        onSubmit={handleCancelWorkOrder}
        error={error?.[VantifyOperation.CANCEL_WORK_ORDER] || null}
        isLoading={loading[VantifyOperation.CANCEL_WORK_ORDER] || false}
      />
    </>
  );
};